import { JobTitle } from '@pflegia/data-access';
import { CandidatePriority, E_PRIORITIES } from '@pflegia/types/api/candidate/candidate-priority';
import { CaregiverQualification } from '@pflegia/types/api/candidate/care-giver-qualification';
import { CareType } from '@pflegia/types/api/employer/care-type';
import { FunnelQuestion, FunnelQuestionCategory } from '@pflegia/types/api/funnel/questions';
import { ConfirmationStatus } from '@pflegia/types/api/general/confirmation-status';
import { COMMON_DOWNGRADES, DowngradeFn, JobTitleCategory, JobTitleEnum, RequiredJobTitle } from '@pflegia/types/api/job-title';
import { ErrorType, PrioritizationError } from '@pflegia/types/api/job-title/multiposting/errors';
import { LocalFunnelStep } from '@pflegia/ui/funnel/types';
import { FunnelAnswers } from '@pflegia/ui/pflegia/funnel/types/funnel-answers';
import { assertIsDefined } from '@pflegia/util/functions/type-guards';
import { getMainPriorityJobTitle } from '@pflegia/util/pflegia/job-title';

import { findDowngradedJobTitles } from '../helpers/multiposting/find-downgraded-job-titles';
import { getFunnelPropsData } from '../helpers/multiposting/get-funnel-props-data';
import { FunnelProps } from '../hooks/use-funnel-state';

type JobPositionQualificationDowngradeFn<A = ConfirmationStatus> = DowngradeFn<A, { employerCareType: CareType; isPraxia: boolean }>;

const getNewJobTitleForHighPrios: LocalFunnelStep<
  JobTitle,
  FunnelAnswers,
  FunnelQuestion.JobQualification,
  FunnelProps
>['getNewJobTitle'] = (answer, answers, jobTitles, props) => {
  if (answer === ConfirmationStatus.Yes) return null;

  const selectedJobTitles = answers[FunnelQuestion.PositionDesired]?.map(({ jobTitle }) => jobTitle) as RequiredJobTitle[];
  assertIsDefined(selectedJobTitles, 'Selected job titles');

  const mainPriorityJobTitle = getMainPriorityJobTitle(selectedJobTitles);
  assertIsDefined(mainPriorityJobTitle, 'Main priority job title');
  const { employerCareType, isPraxia } = getFunnelPropsData(props as FunnelProps);
  // @ts-expect-error Answer is confirming to its type
  const downgradedJobTitles = getDowngradedJobTitle(mainPriorityJobTitle, answer, { employerCareType, isPraxia });

  const newJobTitles = findDowngradedJobTitles(downgradedJobTitles, jobTitles, isPraxia);
  return newJobTitles;
};

const getDowngradedJobTitle: JobPositionQualificationDowngradeFn = (mainPriorityJobTitle, answer, props) => {
  switch (mainPriorityJobTitle.jobTitle?.trackingPriority) {
    case CandidatePriority.A:
      return getDowngradePathForA(mainPriorityJobTitle, answer, props);
    case CandidatePriority.B:
    case CandidatePriority.B2:
    case CandidatePriority.B3:
    case CandidatePriority.B4:
    case CandidatePriority.B5:
    case CandidatePriority.B6:
      return getDowngradePathForB(mainPriorityJobTitle, answer, props);
    case CandidatePriority.C:
      return getDowngradePathForC(mainPriorityJobTitle, answer, props);
  }
  return null;
};

const getDowngradePathForA: JobPositionQualificationDowngradeFn = (mainPriorityJobTitle, answer, props) => {
  const { employerCareType, isPraxia } = props;
  if (mainPriorityJobTitle.category === JobTitleCategory.Praxia) {
    if (answer === ConfirmationStatus.No) {
      const isPhysioCareType = employerCareType === CareType.Physiotherapy;
      const isDental = employerCareType === CareType.DentalPractice;
      if (isPhysioCareType) return [JobTitleEnum.PhysicalTherapist];
      if (isDental) return [JobTitleEnum.QualifiedDentalEmployee];
      return [JobTitleEnum.MedicalAssistant];
    }
    return null;
  }
  if (answer === ConfirmationStatus.InProgress)
    return isPraxia ? COMMON_DOWNGRADES.Medical.Assistants : COMMON_DOWNGRADES.Nursing.CareTakers;

  if (answer === ConfirmationStatus.No) return isPraxia ? COMMON_DOWNGRADES.Medical.Assistants : COMMON_DOWNGRADES.Nursing.Assistants;
  return isPraxia ? COMMON_DOWNGRADES.Medical.Assistants : COMMON_DOWNGRADES.Nursing.CareTakers;
};

const getDowngradePathForB: JobPositionQualificationDowngradeFn = (mainPriorityJobTitle, answer, props) => {
  const { isPraxia } = props ?? {};
  if (answer !== ConfirmationStatus.No) return null;
  const { trackingPriority } = mainPriorityJobTitle.jobTitle;
  switch (trackingPriority) {
    case CandidatePriority.B:
      return isPraxia ? [JobTitleEnum.MedicalAssistance] : COMMON_DOWNGRADES.Nursing.Assistants;
    case CandidatePriority.B2:
    case CandidatePriority.B3:
    case CandidatePriority.B4:
    case CandidatePriority.B5:
    case CandidatePriority.B6:
      return [JobTitleEnum.MedicalAssistance];
  }
  throw new PrioritizationError(ErrorType.UnknownPriority, mainPriorityJobTitle);
};

const getDowngradePathForC: JobPositionQualificationDowngradeFn<CaregiverQualification | ConfirmationStatus> = (
  mainPriorityJobTitle,
  answer,
  props,
) => {
  const { isPraxia } = props ?? {};

  if (mainPriorityJobTitle.category === JobTitleCategory.Companion) {
    if (answer === CaregiverQualification.Searching) return [JobTitleEnum.Apprenticeship];
  }
  if (answer === ConfirmationStatus.No) return isPraxia ? COMMON_DOWNGRADES.Medical.Assistants : [JobTitleEnum.AssistantNurse];
  return null;
};

export const jobPositionQualificationStep: LocalFunnelStep<JobTitle, FunnelAnswers, FunnelQuestion.JobQualification> = {
  category: FunnelQuestionCategory.Position,
  key: FunnelQuestion.JobQualification,
  condition: answers => {
    const selectedJobTitles = answers[FunnelQuestion.PositionDesired]?.map(({ jobTitle }) => jobTitle) as RequiredJobTitle[];
    const mainPriorityJobTitle = getMainPriorityJobTitle(selectedJobTitles);
    assertIsDefined(mainPriorityJobTitle, 'Main priority job title');
    return !E_PRIORITIES.includes(mainPriorityJobTitle.jobTitle.trackingPriority);
  },
  getNewJobTitle: getNewJobTitleForHighPrios,
};
